import { GridColDef, GridColumnResizeParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useRef, useState } from 'react';

type GridApiReference = ReturnType<typeof useGridApiRef>;

export const useColumnWidths = (columns: GridColDef[], apiReference: GridApiReference) => {
  const [lockedWidths, setLockedWidths] = useState<Record<string, number | undefined>>({});
  const renderedLayoutReference = useRef<Record<string, number>>({});

  const captureRenderedLayout = useCallback(() => {
    if (Object.keys(renderedLayoutReference.current).length > 0) return;
    const visibleColumns = apiReference.current?.getVisibleColumns() ?? [];
    renderedLayoutReference.current = Object.fromEntries(
      visibleColumns.map(column => [column.field, column.computedWidth])
    );
  }, [apiReference]);

  const onColumnWidthChange = useCallback(
    ({ colDef, width }: GridColumnResizeParams) => {
      captureRenderedLayout();
      const renderedWidth = apiReference.current?.getColumn(colDef.field)?.computedWidth ?? width;
      setLockedWidths(previous => ({
        ...renderedLayoutReference.current,
        ...previous,
        [colDef.field]: renderedWidth,
      }));
    },
    [apiReference, captureRenderedLayout]
  );

  const columnsWithLockedWidths = useMemo(
    () =>
      columns.map(column => {
        const lockedWidth = lockedWidths[column.field];
        return lockedWidth === undefined ? column : { ...column, width: lockedWidth, flex: 0 };
      }),
    [columns, lockedWidths]
  );

  return { columns: columnsWithLockedWidths, onColumnResize: captureRenderedLayout, onColumnWidthChange };
};
